/**
 * RosterDrawer — the seat-card grid's successor (8P program Phase 1).
 *
 * The walkable hall + nameplates are the PRIMARY roster now; this drawer
 * is the always-available DOM surface that
 *   1. screen readers get (the canvas is invisible to them),
 *   2. becomes the whole roster UI when WebGL fails (Lobby3DBoundary
 *      renders nothing and the 2D backdrop shows), and
 *   3. hosts the actions that need reliable hit targets (profile / kick).
 *
 * Collapsed by default to a slim "Party N/M" pill; expands to a compact
 * list. Scales to any player count — it's a list, not a card grid.
 */
import { useState } from 'react';
import type { LobbyPlayerView } from '@tt/lobby';
import { MAX_PLAYERS } from '@tt/players';
import { IconAvatar } from '../characters/IconAvatar';
import { titleRarityClass } from '../me/titleRarity';

interface Props {
  players: LobbyPlayerView[];
  /** The host's table-size cap (2-8) — falls back to the platform ceiling
   *  if omitted (e.g. an older lobby snapshot mid-migration). */
  maxPlayers?: number;
  meCharId: string;
  isHost: boolean;
  onInspect: (charId: string) => void;
  onKick: (charId: string) => void;
}

export function RosterDrawer({ players, maxPlayers, meCharId, isHost, onInspect, onKick }: Props) {
  const [open, setOpen] = useState(false);
  const cap = maxPlayers ?? MAX_PLAYERS;
  const seated = players.filter((p) => !p.isSpectator);
  const spectators = players.filter((p) => p.isSpectator);

  return (
    <section className={`roster-drawer${open ? ' open' : ''}`}>
      <button
        type="button"
        className="roster-drawer-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`Party roster: ${seated.length} of ${cap} seated${spectators.length ? `, ${spectators.length} watching` : ''}`}
      >
        <span aria-hidden="true">🪑</span>
        <span>Party {seated.length}/{cap}</span>
        {spectators.length > 0 && <span className="roster-drawer-specs">+{spectators.length} 👁</span>}
        <span className="roster-drawer-chev" aria-hidden="true">{open ? '▾' : '▴'}</span>
      </button>
      {open && (
        <ul className="roster-drawer-list">
          {seated.map((p) => (
            <li key={p.charId} className={`roster-row${p.isDisconnected ? ' offline' : ''}`}>
              <IconAvatar icon={p.icon} size={26} alt="" />
              <span className="roster-row-name">
                {p.name}{p.charId === meCharId ? ' (you)' : ''}
              </span>
              {p.title && p.title !== 'Tavern Regular' && (
                <span className={`roster-row-title ${titleRarityClass(p.title)}`}>{p.title}</span>
              )}
              <span className="roster-row-tags">
                {p.isHost && <span className="tag host">host</span>}
                {p.isDisconnected
                  ? <span className="tag muted">reconnecting…</span>
                  : p.ready
                    ? <span className="tag ready">ready</span>
                    : <span className="tag muted">…</span>}
                <span className="tag muted">{p.lobbyScore}</span>
              </span>
              <span className="roster-row-actions">
                <button type="button" className="link" onClick={() => onInspect(p.charId)}>profile</button>
                {isHost && p.charId !== meCharId && (
                  <button type="button" className="link kick-link" onClick={() => onKick(p.charId)}>kick</button>
                )}
              </span>
            </li>
          ))}
          {spectators.map((p) => (
            <li key={p.charId} className="roster-row spectator">
              <IconAvatar icon={p.icon} size={26} alt="" />
              <span className="roster-row-name">{p.name}</span>
              <span className="roster-row-tags"><span className="tag muted">watching</span></span>
              <span className="roster-row-actions">
                <button type="button" className="link" onClick={() => onInspect(p.charId)}>profile</button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
